import * as React from "react";
import { graphql, Link } from "gatsby";
import Header from "../components/page-layout/header/Header";
import { Trans, useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import SEO from "../components/SEO";
import PageLayout from "../components/page-layout/PageLayout";
import TextHero from "../components/hero/TextHero";
import SideGridBlock from "../components/grid-block/SideGridBlock";
import TextSection from "../components/text/TextSection";
import BodyText from "../components/typography/BodyText";
import {
  ExclamationIcon,
  ArrowDownIcon,
  CursorClickIcon,
} from "@heroicons/react/outline";
import { twMerge } from "tailwind-merge";
import GridBlock from "../components/grid-block/GridBlock";
import { HeroCta } from "../components/cta/HeroCta";
import Keyword from "../components/typography/Keyword";
import { LinkCta } from "../components/typography/LinkCta";
import { useUseCaseI18nLink } from "../hooks/useI18nLink";
import { useUseCases } from "../hooks/useUseCases";
import IconHero from "../components/hero/IconHero";
import ImageCta from "../components/cta/ImageCta";
import SideNumberedList from "../components/grid-block/SideNumberedList";
import { TestimonialsTopCta } from "../components/cta/TestimonialsCta";

const NextArrow = (props: { dark?: boolean; border?: boolean }) => (
  <div
    className={twMerge(
      "z-50 -my-8 flex items-center",
      props.border && "h-[1px] bg-gray-700",
    )}
  >
    <div
      className={twMerge(
        "h-16 w-16 rounded-full border border-indigo-500 bg-indigo-100 mx-auto flex items-center justify-center",
        props.dark
          ? "bg-gray-800 border-gray-600 text-gray-100"
          : "border-gray-300 bg-gray-50 text-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100",
      )}
    >
      <ArrowDownIcon className="w-1/2 h-1/2 " />
    </div>
  </div>
);

const MethodPage = () => {
  const { t } = useTranslation();
  const { language } = useI18next();
  const { useCaseLink } = useUseCaseI18nLink();
  const useCase = useUseCases();

  return (
    <PageLayout darkHeader>
      <SEO
        titleI18nKey={"method:meta.title"}
        descriptionI18nKey={"method:meta.description"}
        datePublished={new Date("2021-03-01T08:00:00Z")}
      />

      <IconHero
        tag={t("method:hero.tag")}
        title={
          <Trans
            i18nKey="method:hero.title"
            components={{ olg: <span className="text-ol-gradient"></span> }}
          />
        }
        topCta={<TestimonialsTopCta theme="dark" />}
        cta={<HeroCta scheduleCallCta theme="dark" />}
        description={t("method:hero.description")}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="#1f2937"
            stroke="#4b5563"
            strokeWidth={0.1}
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M9.504 1.132a1 1 0 01.992 0l1.75 1a1 1 0 11-.992 1.736L10 3.152l-1.254.716a1 1 0 11-.992-1.736l1.75-1zM5.618 4.504a1 1 0 01-.372 1.364L5.016 6l.23.132a1 1 0 11-.992 1.736L4 7.723V8a1 1 0 01-2 0V6a.996.996 0 01.52-.878l1.734-.99a1 1 0 011.364.372zm8.764 0a1 1 0 011.364-.372l1.733.99A1.002 1.002 0 0118 6v2a1 1 0 11-2 0v-.277l-.254.145a1 1 0 11-.992-1.736l.23-.132-.23-.132a1 1 0 01-.372-1.364zm-7 4a1 1 0 011.364-.372L10 8.848l1.254-.716a1 1 0 11.992 1.736L11 10.58V12a1 1 0 11-2 0v-1.42l-1.246-.712a1 1 0 01-.372-1.364zM3 11a1 1 0 011 1v1.42l1.246.712a1 1 0 11-.992 1.736l-1.75-1A1 1 0 012 14v-2a1 1 0 011-1zm14 0a1 1 0 011 1v2a1 1 0 01-.504.868l-1.75 1a1 1 0 11-.992-1.736L16 13.42V12a1 1 0 011-1zm-9.618 5.504a1 1 0 011.364-.372l.254.145V16a1 1 0 112 0v.277l.254-.145a1 1 0 11.992 1.736l-1.735.992a.995.995 0 01-1.022 0l-1.735-.992a1 1 0 01-.372-1.364z"
              clipRule="evenodd"
            />
          </svg>
        }
      />

      <SideGridBlock
        titleBgTag
        half={false}
        tag={t("method:phase1.tag")}
        title={t("method:phase1.title")}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: t("method:phase1.description"),
            }}
          />
        }
        children={<HeroCta />}
        singleColumn
        blocks={(
          t("method:phase1.blocks", { returnObjects: true }) as Array<{
            title: string;
            description: string;
          }>
        ).map((block) => (
          <div>
            <h3 className="text-2xl font-bold mb-2">{block.title}</h3>
            <p
              className="text-gray-600 dark:text-gray-400 prose dark:prose-invert text-justify"
              dangerouslySetInnerHTML={{ __html: block.description }}
            />
          </div>
        ))}
      />
      <NextArrow />

      <SideGridBlock
        titleBgTag
        half={false}
        theme="gray"
        tag={t("method:phase2.tag")}
        title={t("method:phase2.title")}
        blocks={(
          t("method:phase2.blocks", { returnObjects: true }) as Array<{
            title: string;
            description: string;
          }>
        ).map((block) => (
          <div>
            <h3 className="text-2xl font-bold mb-2">{block.title}</h3>
            <p
              className="text-gray-600 dark:text-gray-400 prose dark:prose-invert text-justify"
              dangerouslySetInnerHTML={{ __html: block.description }}
            />
          </div>
        ))}
        singleColumn
      />

      <div className="z-50 -my-6 px-8">
        <div className="h-12 px-8 max-w-[20rem] rounded-full border border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 mx-auto flex items-center justify-center">
          <p className="text-md text-gray-900 dark:text-gray-100 text-center">
            <span className="text-violet-600 dark:text-violet-400">for</span>{" "}
            milestone{" "}
            <span className="text-violet-600 dark:text-violet-400">in</span>{" "}
            milestones:
          </p>
        </div>
      </div>

      <TextSection
        theme="dark"
        titleBgTag
        tag={t("method:quote.tag")}
        title={t("method:quote.title")}
        text={
          <span
            dangerouslySetInnerHTML={{ __html: t("method:quote.description") }}
          />
        }
      />
      <NextArrow dark border />

      <SideGridBlock
        theme="dark"
        half={false}
        titleBgTag
        tag={t("method:development.tag")}
        title={t("method:development.title")}
        blocks={(
          t("method:development.blocks", { returnObjects: true }) as Array<{
            title: string;
            description: string;
          }>
        ).map((block) => (
          <div>
            <h3 className="text-white text-2xl font-bold mb-2">
              {block.title}
            </h3>
            <p
              className="text-gray-300 prose prose-invert text-justify"
              dangerouslySetInnerHTML={{ __html: block.description }}
            />
          </div>
        ))}
        singleColumn
      />
      <NextArrow dark border />

      <TextSection
        theme="dark"
        titleBgTag
        tag={t("method:acceptance.tag")}
        title={t("method:acceptance.title")}
        text={
          <span
            dangerouslySetInnerHTML={{
              __html: t("method:acceptance.description"),
            }}
          />
        }
        children={
          <div className="rounded-lg bg-indigo-900 border border-indigo-600 p-4 flex flex-col md:flex-row md:items-center">
            <ExclamationIcon className="w-10 text-indigo-200 mr-4 shrink-0" />
            <BodyText className="text-md mb-0" dark>
              <span
                dangerouslySetInnerHTML={{
                  __html: t("method:acceptance.callout"),
                }}
              />
            </BodyText>
          </div>
        }
      />
      <div className="z-50 -my-6 h-12 w-32 px-8 rounded-full border border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 mx-auto flex items-center justify-center">
        <p className="text-md text-violet-600 dark:text-violet-400 text-center">
          end
        </p>
      </div>

      <SideGridBlock
        titleBgTag
        theme="gray"
        tag={t("method:evolution.tag")}
        title={t("method:evolution.title")}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: t("method:evolution.description"),
            }}
          />
        }
        singleColumn
        blocks={(
          t("method:evolution.blocks", { returnObjects: true }) as Array<string>
        ).map((block) => (
          <div>
            <p
              className="text-justify mb-2"
              dangerouslySetInnerHTML={{ __html: block }}
            />
            <hr className="w-2/12 my-2 border-primary border-2 rounded-3xl" />
          </div>
        ))}
      />

      <SideNumberedList
        tag={t(`method:whyItWorks.tag`)}
        title={t(`method:whyItWorks.title`)}
        description={t(`method:whyItWorks.description`)}
        items={t("method:whyItWorks.items", { returnObjects: true }) ?? []}
      />

      {/* CTA */}
      <ImageCta
        tag={t(`method:cta.tag`)}
        title={t(`method:cta.title`)}
        description={t(`method:cta.description`)}
        image={require("../assets/images/astrorei-cta.jpg").default}
      />

      {/* USE CASES */}
      <GridBlock
        tag={t(`method:useCase.tag`)}
        title={t(`method:useCase.title`)}
        description={t(`method:useCase.description`)}
        lg3={false}
        blocks={useCase.slice(0, 4).map((useCase) => (
          <>
            <Keyword
              title={useCase.hero.projectName[language]}
              description={useCase.hero.projectName[language]}
            />
            <LinkCta
              to={useCaseLink(useCase)}
              text={t("index:useCase.ctaLink")}
              align="right"
            />
          </>
        ))}
      />
    </PageLayout>
  );
};

export default MethodPage;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
